import Card from "components/card";
import Loader from "components/loader";
import useStats from "hooks/useStats";
import { useEffect, useMemo } from "react";
import Chart from "react-apexcharts";
import { DASHBOARD_COLORS } from "utils/dashboardColors";

const MESES_ABREV = [
  "Ene", "Feb", "Mar", "Abr", "May", "Jun",
  "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
];

const LAST_N_MONTHS = 12;

const formatMonthLabel = (mes) => {
  const [year, month] = String(mes).split("-").map(Number);
  if (!year || !month) return mes;
  return `${MESES_ABREV[month - 1]} ${String(year).slice(-2)}`;
};

const PostulationsMonthlyChart = () => {
  const { isLoading, response, fetchData } = useStats("publicaciones-mensual");

  useEffect(() => {
    fetchData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mensual = useMemo(() => {
    const payload = response?.mensual ? response : response?.data || {};
    const rows = Array.isArray(payload?.mensual) ? payload.mensual : [];

    return [...rows]
      .filter((item) => item?.mes)
      .sort((a, b) => (a.mes > b.mes ? 1 : -1))
      .slice(-LAST_N_MONTHS);
  }, [response]);

  const peak = useMemo(() => {
    if (mensual.length === 0) return null;
    return mensual.reduce(
      (max, item) => (Number(item.postulaciones_nuevas || 0) > Number(max.postulaciones_nuevas || 0) ? item : max),
      mensual[0]
    );
  }, [mensual]);

  const chartSeries = useMemo(
    () => [
      { name: "Publicaciones nuevas", type: "column", data: mensual.map((item) => Number(item?.publicaciones_nuevas || 0)) },
      { name: "Postulaciones nuevas", type: "line", data: mensual.map((item) => Number(item?.postulaciones_nuevas || 0)) },
    ],
    [mensual]
  );

  const chartOptions = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
      },
      stroke: { width: [0, 3], curve: "smooth" },
      colors: [DASHBOARD_COLORS.instituciones, DASHBOARD_COLORS.postulantes],
      dataLabels: { enabled: false },
      xaxis: {
        categories: mensual.map((item) => formatMonthLabel(item.mes)),
        labels: { style: { colors: "#A3AED0", fontSize: "12px" } },
      },
      yaxis: [
        {
          title: { text: "Publicaciones", style: { color: "#A3AED0" } },
          labels: { style: { colors: "#A3AED0", fontSize: "12px" } },
        },
        {
          opposite: true,
          title: { text: "Postulaciones", style: { color: "#A3AED0" } },
          labels: { style: { colors: "#A3AED0", fontSize: "12px" } },
        },
      ],
      legend: { show: true, position: "top" },
      grid: { show: false },
      tooltip: { theme: "dark" },
    }),
    [mensual]
  );

  return (
    <Card extra="!p-[20px] h-[500px]">
      <div className="text-xl font-bold text-navy-700 dark:text-white">
        Publicaciones y postulaciones por mes
      </div>

      {!isLoading && peak && Number(peak.postulaciones_nuevas || 0) > 0 && (
        <p className="mt-1 mb-2 text-xs text-gray-600 dark:text-gray-400">
          Mes pico:{" "}
          <span className="font-bold text-navy-700 dark:text-white">
            {formatMonthLabel(peak.mes)}
          </span>{" "}
          con <span className="font-bold text-navy-700 dark:text-white">{peak.postulaciones_nuevas}</span> postulaciones.
        </p>
      )}

      {isLoading ? (
        <div className="flex h-[420px] items-center justify-center">
          <Loader />
        </div>
      ) : mensual.length === 0 ? (
        <p className="text-sm text-gray-600 dark:text-gray-400">No hay datos disponibles</p>
      ) : (
        <div className="h-[360px] w-full">
          <Chart options={chartOptions} series={chartSeries} type="line" width="100%" height="100%" />
        </div>
      )}
    </Card>
  );
};

export default PostulationsMonthlyChart;
